import type { Project } from '../lib/api';
import { formatDate, formatPercent, formatStatus } from '../lib/format';

/**
 * §0.3 table contract: navy header, 11px uppercase labels, zebra rows,
 * #EBF0F8 hover. §0.5: every numeric cell is JetBrains Mono with
 * tabular-nums, applied by the `.numeric` class.
 *
 * §0.6: at the base breakpoint the table becomes a stacked card list rather
 * than a horizontally scrolling grid, because there is no horizontal overflow
 * at 375px.
 */
export function ProjectTable({ projects }: { projects: Project[] }): JSX.Element {
  if (projects.length === 0) {
    return (
      <div className="card rounded-card border border-border bg-white p-6">
        <p className="text-body text-text-secondary">
          No projects are visible in the current entity scope.
        </p>
      </div>
    );
  }

  return (
    <>
      {/* base breakpoint: stacked cards */}
      <ul className="grid gap-2 lg:hidden">
        {projects.map((project) => (
          <li key={project.id} className="card rounded-card border border-border bg-white p-4">
            <div className="flex items-baseline justify-between gap-3">
              <a href={`#/projects/${project.id}`} className="numeric text-numeric text-royal underline">
                {project.code}
              </a>
              <span className="micro-label text-text-secondary">{project.pillar}</span>
            </div>
            <p className="mt-1 text-h3 font-semibold text-text-primary">{project.name}</p>
            <p className="mt-1 text-body text-text-secondary">{formatStatus(project.status)}</p>
            <p className="numeric mt-2 text-numeric text-text-secondary">
              {formatPercent(project.percent_complete)} complete
            </p>
          </li>
        ))}
      </ul>

      {/* lg and up: the full grid */}
      <div className="hidden overflow-hidden rounded-card border border-border lg:block">
        <table className="w-full border-collapse text-body">
          <caption className="sr-only">Projects in the current entity scope</caption>
          <thead>
            <tr className="table-header bg-navy text-white">
              <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Code</th>
              <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Project</th>
              <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Entity</th>
              <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Status</th>
              <th scope="col" className="px-3 py-2 text-right text-micro font-medium uppercase">Complete</th>
              <th scope="col" className="px-3 py-2 text-left text-micro font-medium uppercase">Location</th>
              <th scope="col" className="px-3 py-2 text-right text-micro font-medium uppercase">Updated</th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr key={project.id} className="table-row border-t border-border">
                <td className="numeric px-3 py-2 text-numeric">
                  {/* The code is the way into the project's own screen, where
                      §4.5's status gates and the hold live. */}
                  <a href={`#/projects/${project.id}`} className="text-royal underline">
                    {project.code}
                  </a>
                </td>
                <td className="px-3 py-2 font-medium text-text-primary">{project.name}</td>
                <td className="px-3 py-2 text-text-secondary">{project.pillar}</td>
                <td className="px-3 py-2 text-text-secondary">{formatStatus(project.status)}</td>
                <td className="numeric px-3 py-2 text-right text-numeric">
                  {formatPercent(project.percent_complete)}
                </td>
                <td className="px-3 py-2 text-text-secondary">
                  {[project.site_city, project.site_state].filter(Boolean).join(', ') || '—'}
                </td>
                <td className="numeric px-3 py-2 text-right text-numeric text-text-secondary">
                  {formatDate(project.updated_at)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
